"use client";

import React, { useState } from "react";
import {
  CheckCircle2, ChevronDown, ShieldCheck,
  Search, Wrench, Phone, ArrowRight, Clock,
} from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const INSPECTION_STEPS = [
  {
    step: "01",
    title: "Kiểm tra cần đàn",
    short: "Phát hiện cong vênh, relief sai chuẩn",
    detail:
      "Nhìn dọc theo cần đàn từ đầu về thùng để phát hiện cong ngửa, cong úp hoặc xoắn. Đo độ relief bằng capo và thước feeler. Cần thẳng hoặc relief 0.2–0.4mm là lý tưởng.",
    icon: "🎸",
    tag: "Cấu trúc",
  },
  {
    step: "02",
    title: "Kiểm tra action",
    short: "Đo chiều cao dây tại ngưỡng 12",
    detail:
      "Đo khoảng cách từ đáy dây đến mặt phím tại phím 12. Guitar acoustic: dây 1 ≈ 2mm, dây 6 ≈ 2.5mm. Action cao gây khó bấm; action thấp gây rè buzz.",
    icon: "📏",
    tag: "Thiết lập",
  },
  {
    step: "03",
    title: "Kiểm tra phím đàn",
    short: "Mòn, gỉ, phím cao thấp không đều",
    detail:
      "Dùng thước thẳng kiểm tra từng cụm 3 phím liên tiếp. Phím mòn hình chữ V hoặc lõm giữa sẽ gây âm chết. Gỉ nhẹ có thể đánh bóng lại; mòn sâu phải thay mới.",
    icon: "🔩",
    tag: "Phím đàn",
  },
  {
    step: "04",
    title: "Kiểm tra thùng đàn",
    short: "Nứt gỗ, tách keo, phồng mặt",
    detail:
      "Kiểm tra toàn bộ bề mặt dưới ánh sáng nghiêng để phát hiện vết nứt nhỏ. Đặc biệt chú ý góc cần – thùng, viền hông và khu vực xung quanh lỗ thoát âm. Mặt đàn phồng lên là dấu hiệu đã từng bị ẩm nặng.",
    icon: "🪵",
    tag: "Thân đàn",
  },
  {
    step: "05",
    title: "Test âm thanh thực tế",
    short: "Nghe tone, sustain, cân bằng dây",
    detail:
      "Chơi từng dây mở, sau đó bấm hợp âm ở các vị trí phím 1, 5, 7, 12. Nghe độ sustain, balance giữa các dây, kiểm tra buzz. Đánh các chord phức tạp để cảm nhận intonation thực tế.",
    icon: "🎵",
    tag: "Âm thanh",
  },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Chuyên gia 10+ năm kinh nghiệm" },
  { icon: Search,      label: "Kiểm tra 40+ điểm chi tiết" },
  { icon: Wrench,      label: "Báo cáo kỹ thuật minh bạch" },
  { icon: Clock,       label: "Hoàn thành trong 30–60 phút" },
];

const QUALITY_BARS = [
  { label: "Cấu trúc cần đàn",   pct: 95 },
  { label: "Tình trạng phím đàn", pct: 88 },
  { label: "Chất lượng âm thanh", pct: 100 },
  { label: "Tình trạng thùng gỗ", pct: 92 },
];

const MINI_STATS = [
  { value: "500+", label: "Đàn đã kiểm tra" },
  { value: "10+",  label: "Năm kinh nghiệm" },
  { value: "40+",  label: "Điểm kiểm tra" },
  { value: "100%", label: "Minh bạch" },
];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const GuitarInspectionServiceSection: React.FC = () => {
  const [openStep, setOpenStep] = useState<number | null>(0);

  return (
    <section
      aria-labelledby="inspection-heading"
      className="w-full overflow-hidden py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundColor: "var(--section-bg)",
        color: "var(--section-foreground)",
        fontFamily: "var(--font-roboto)",
      }}
    >
      <div className="max-w-7xl mx-auto">

        {/* ══════════════════════════════════════
            TOP SECTION: intro + trust card
        ══════════════════════════════════════ */}
        <div className="lg:grid lg:grid-cols-[1fr_340px] lg:gap-12 xl:gap-16 mb-10 sm:mb-14 items-start">

          {/* ── LEFT ── */}
          <div className="mb-8 lg:mb-0">




            {/* Heading */}
<h2
  id="inspection-heading"
  className="mb-4 sm:mb-5 px-0.5"
  style={{ fontFamily: "var(--font-montserrat)", wordBreak: "keep-all" }}
>
  {/* Tier 1: eyebrow — pill badge, rõ hơn trên nền trắng */}
  <span
    className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase mb-3 sm:mb-4 px-3 py-1.5 rounded-full border"
    style={{
      color: "#92702A",                          /* gold đậm hơn — đủ tương phản trên trắng */
      backgroundColor: "rgba(200,169,110,0.1)",
      borderColor: "rgba(200,169,110,0.35)",
    }}
  >
    <span
      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: "#92702A" }}
    />
    Dịch Vụ Kiểm Tra
  </span>

  {/* Tier 2: hero text — dark on white, stroke accent */}
  <span className="block leading-[1.05] tracking-tight mb-2 sm:mb-3">
    <span
      className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black"
      style={{ color: "#1a1a1a" }}              /* gần đen — tương phản tối đa */
    >
      Guitar{" "}
    </span>
    {/* "Cũ" — gold với text-stroke effect qua drop shadow */}
    <span
      className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black"
      style={{
        color: "#92702A",                        /* gold đậm đủ nhìn trên trắng */
        WebkitTextStroke: "0.5px rgba(146,112,42,0.3)",
      }}
    >
      Cũ
    </span>
  </span>

  {/* Divider line — visual separator thay vì chỉ dùng margin */}
  <span
    className="block w-10 sm:w-14 h-0.5 rounded-full mb-2.5 sm:mb-3"
    style={{ backgroundColor: "rgba(200,169,110,0.5)" }}
  />

  {/* Tier 3: subtitle — foreground đủ đậm + & gold */}
  <span
    className="block text-sm sm:text-base md:text-lg leading-snug"
    style={{ color: "#555", fontWeight: 400 }}  /* xám trung tính — dễ đọc trên trắng */
  >
    <span
      className="font-black mr-2 text-base sm:text-lg md:text-xl"
      style={{ color: "#92702A" }}
    >
      &amp;
    </span>
    Guitar Đã Qua Sử Dụng
  </span>
</h2>

            {/* Body */}
            <p
              className="text-sm sm:text-base lg:text-lg leading-relaxed max-w-2xl mb-7 sm:mb-8"
              style={{ color: "var(--section-muted)" }}
            >
              Trước khi mua guitar cũ, việc kiểm tra cần được thực hiện kỹ lưỡng để tránh{" "}
              <strong style={{ color: "var(--section-foreground)" }}>lỗi cần cổ, cong phím, nứt gỗ</strong>{" "}
              hoặc mất ổn định âm thanh. Guitar Sài Gòn hỗ trợ khách hàng tại{" "}
              <strong style={{ color: "var(--section-foreground)" }}>Quận 12 – TPHCM</strong>{" "}
              kiểm tra toàn diện guitar đã qua sử dụng.
            </p>

            {/* ── Quality progress bars — desktop left panel ── */}
            <div
              className="hidden sm:block rounded-2xl border p-5"
              style={{
                backgroundColor: "var(--section-card)",
                borderColor: "var(--section-border)",
              }}
            >
              <p
                className="text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: "var(--section-muted)", fontFamily: "var(--font-montserrat)" }}
              >
                Mức độ kiểm tra tiêu chuẩn
              </p>
              <div className="space-y-3.5">
                {QUALITY_BARS.map(({ label, pct }) => (
                  <div key={label}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs" style={{ color: "var(--section-foreground)" }}>{label}</span>
                      <span
                        className="text-xs font-black"
                        style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                      >
                        {pct}%
                      </span>
                    </div>
                    <div
                      className="h-1.5 rounded-full overflow-hidden"
                      style={{ backgroundColor: "rgba(200,169,110,0.1)" }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: "linear-gradient(to right, var(--brand-primary), rgba(200,169,110,0.45))",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT: trust card ── */}
          <div
            className="rounded-2xl border overflow-hidden"
            style={{
              backgroundColor: "var(--section-card)",
              borderColor: "rgba(200,169,110,0.28)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.07), 0 0 0 1px rgba(200,169,110,0.05)",
            }}
          >
            {/* Gold top bar */}
            <div
              className="h-1"
              style={{ background: "linear-gradient(to right, var(--brand-primary), rgba(200,169,110,0.3))" }}
            />

            <div className="p-5 sm:p-6">

              <p
                className="text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Cam kết của chúng tôi
              </p>

              {/* Trust items */}
              <ul className="space-y-3 mb-5">
                {TRUST_BADGES.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3">
                    <div
                      className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center"
                      style={{
                        backgroundColor: "rgba(200,169,110,0.1)",
                        border: "1.5px solid rgba(200,169,110,0.25)",
                      }}
                    >
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: "var(--brand-primary)" }} />
                    </div>
                    <span className="text-xs sm:text-sm font-medium" style={{ color: "var(--section-foreground)" }}>
                      {label}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Divider */}
              <div className="h-px mb-4" style={{ backgroundColor: "var(--section-border)" }} />

              {/* Mini stats 2×2 grid */}
              <div className="grid grid-cols-2 gap-2 mb-5">
                {MINI_STATS.map(({ value, label }) => (
                  <div
                    key={label}
                    className="rounded-xl py-3 px-2 text-center border"
                    style={{
                      backgroundColor: "var(--section-bg)",
                      borderColor: "var(--section-border)",
                    }}
                  >
                    <p
                      className="text-base sm:text-lg font-black leading-none mb-0.5"
                      style={{ fontFamily: "var(--font-montserrat)", color: "var(--brand-primary)" }}
                    >
                      {value}
                    </p>
                    <p className="text-[10px] sm:text-[11px] leading-tight" style={{ color: "var(--section-muted)" }}>
                      {label}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA — full width, 52px tap target */}
              <a
                href="tel:0325805893"
                className="group flex items-center justify-center gap-2 w-full rounded-xl font-black text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                style={{
                  backgroundColor: "var(--brand-primary)",
                  color: "var(--brand-primary-foreground)",
                  fontFamily: "var(--font-montserrat)",
                  fontSize: "0.875rem",
                  height: "52px",
                  boxShadow: "0 4px 16px rgba(200,169,110,0.28)",
                }}
              >
                <Phone className="w-4 h-4" />
                Đặt Lịch Kiểm Tra Ngay
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            ACCORDION STEPS
        ══════════════════════════════════════ */}

        {/* Section label */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="h-px flex-1" style={{ backgroundColor: "var(--section-border)" }} />
          <h3
            className="text-[10px] sm:text-xs font-bold uppercase tracking-widest px-2 whitespace-nowrap"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--section-muted)" }}
          >
            5 trong 10 bước kiểm tra tiêu chuẩn
          </h3>
          <div className="h-px flex-1" style={{ backgroundColor: "var(--section-border)" }} />
        </div>

        <div className="space-y-2 sm:space-y-2.5">
          {INSPECTION_STEPS.map((item, i) => {
            const isOpen = openStep === i;
            return (
              <div
                key={i}
                className="rounded-2xl border overflow-hidden transition-all duration-200"
                style={{
                  borderColor: isOpen ? "rgba(200,169,110,0.45)" : "var(--section-border)",
                  backgroundColor: "var(--section-card)",
                  boxShadow: isOpen ? "0 4px 20px rgba(200,169,110,0.08)" : "none",
                }}
              >
                {/* ── Trigger row ── */}
                <button
                  onClick={() => setOpenStep(isOpen ? null : i)}
                  className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 lg:px-6 py-3.5 sm:py-4 lg:py-5 text-left"
                  aria-expanded={isOpen}
                >
                  {/* Step number */}
                  <div
                    className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center text-xs sm:text-sm font-black transition-colors duration-200"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: isOpen ? "var(--brand-primary)" : "rgba(200,169,110,0.1)",
                      color: isOpen ? "var(--brand-primary-foreground)" : "var(--brand-primary)",
                      border: isOpen ? "none" : "1.5px solid rgba(200,169,110,0.25)",
                    }}
                  >
                    {item.step}
                  </div>

                  {/* Emoji */}
                  <span className="text-lg sm:text-xl shrink-0">{item.icon}</span>

                  {/* Title + subtitle */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p
                        className="font-black text-sm sm:text-base leading-snug"
                        style={{ fontFamily: "var(--font-montserrat)", color: "var(--section-foreground)" }}
                      >
                        {item.title}
                      </p>
                      {/* Category tag pill */}
                      <span
                        className="inline-flex px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold border"
                        style={{
                          backgroundColor: "rgba(200,169,110,0.08)",
                          borderColor: "rgba(200,169,110,0.22)",
                          color: "var(--brand-primary)",
                          fontFamily: "var(--font-montserrat)",
                        }}
                      >
                        {item.tag}
                      </span>
                    </div>
                    {!isOpen && (
                      <p className="text-xs sm:text-sm mt-0.5 truncate" style={{ color: "var(--section-muted)" }}>
                        {item.short}
                      </p>
                    )}
                  </div>

                  {/* Chevron */}
                  <ChevronDown
                    className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300"
                    style={{
                      color: "var(--brand-primary)",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                {/* ── Expanded content ── */}
                {isOpen && (
                  <div
                    className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6"
                    style={{ borderTop: "1px solid rgba(200,169,110,0.12)" }}
                  >
                    <div className="pt-3 sm:pt-4 flex gap-3 sm:gap-4">
                      <div
                        className="shrink-0 w-0.5 rounded-full self-stretch"
                        style={{ backgroundColor: "rgba(200,169,110,0.4)" }}
                      />
                      <p className="text-xs sm:text-sm leading-relaxed" style={{ color: "var(--section-muted)" }}>
                        {item.detail}
                      </p>
                    </div>

                    <div className="mt-3 sm:mt-4 flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />
                      <span
                        className="text-[10px] sm:text-xs font-semibold"
                        style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                      >
                        Được thực hiện bởi kỹ thuật viên có chứng nhận
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Bottom note ── */}
        <div
          className="mt-5 sm:mt-7 rounded-2xl px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-4 border"
          style={{
            backgroundColor: "rgba(200,169,110,0.06)",
            borderColor: "rgba(200,169,110,0.2)",
          }}
        >
          <span className="text-xl shrink-0">💡</span>
          <p className="text-xs sm:text-sm leading-relaxed" style={{ color: "var(--section-foreground)" }}>
            <span
              className="font-bold"
              style={{ fontFamily: "var(--font-montserrat)", color: "var(--brand-primary)" }}
            >
              Lưu ý:{" "}
            </span>
            Đây chỉ là 5 trong số 10 bước kiểm tra tiêu chuẩn của Guitar Sài Gòn. Quy trình đầy đủ bao gồm kiểm tra tuning machine, nut, saddle, electronics (nếu có) và độ intonation toàn đàn.
          </p>
        </div>

      </div>
    </section>
  );
};

export default GuitarInspectionServiceSection;